import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, KeyboardAvoidingView, Platform, ActivityIndicator, TouchableOpacity } from 'react-native';
import { Card, Text, Button, ProgressBar, List, Chip, FAB, Portal, Modal, TextInput, Dialog, Paragraph, IconButton } from 'react-native-paper';
import { useRoute, useNavigation, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { HeaderBackButton } from '@react-navigation/elements';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSelector, useDispatch } from 'react-redux';
import { ReviewStackParamList } from '../../navigation/AppNavigator';
import { RootState, AppDispatch } from '../../store';
import { submitReview, completeReview, fetchReviewById, fetchTemplateDetails, createOnDemandItem, updateReviewNotes, updateReviewOnDemandProjects, updateReviewDetails } from '../../store/slices/reviewSlice';
import { colors, spacing, typography, shadows } from '../../constants/theme';
import type { ReviewContactDetails, ReviewItem } from '../../types';
import { SearchBar } from '../../components/SearchBar';
import { SyncStatusBar } from '../../components/SyncStatusBar';
import { StuckSyncWarning } from '../../components/StuckSyncWarning';
import { LoadingScreen } from '../../components/LoadingScreen';
import { OnDemandItemModal } from '../../components/OnDemandItemModal';
import { GeneralNotesModal } from '../../components/GeneralNotesModal';
import { OnDemandProjectsModal } from '../../components/OnDemandProjectsModal';
import { ExpandableFAB } from '../../components/ExpandableFAB';
import { buildHamburgerActions } from '../../utils/buildHamburgerActions';
import { reviewService } from '../../services/reviewService';
import { selectBestResponseForItem } from '../../services/reviewResponseSelector';
import { getEffectiveNetworkOnline } from '../../utils/detoxTestControls';
import { shouldUseOfflineReviewWorkflow } from '../../config/offlineWorkflow';
import { toNavigableReviewItemId } from '../../utils/reviewItemSearch';
import { ReviewDetailsModal } from '../../components/ReviewDetailsModal';

// Define ResponseStatus values directly to avoid import issues
const ResponseStatusValues = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
} as const;

// Define ReviewStatus values directly to avoid import issues
const ReviewStatusValues = {
  DRAFT: 'draft',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  SUBMITTED: 'submitted',
} as const;

// Import MaterialCommunityIcons conditionally for web compatibility
let MaterialCommunityIcons: any;
try {
  MaterialCommunityIcons = require('@expo/vector-icons').MaterialCommunityIcons;
} catch (e) {
  // Fallback for web - create a simple text-based icon component
  const Text = require('react-native').Text;
  MaterialCommunityIcons = ({ name, size, color, ...props }: any) => {
    const iconMap: { [key: string]: string } = {
      'home': '🏠',
      'clipboard-list': '📋',
      'file-document-outline': '📄',
      'check-circle': '✓',
      'clock-outline': '⏱',
      'filter-variant': '▼',
      'magnify': '🔍',
      'chevron-right': '›',
      'circle-outline': '○',
      'check': '✓',
      'alert-circle': '⚠️',
    };
    return <Text style={{ fontSize: size || 24, color }} {...props}>{iconMap[name] || '•'}</Text>;
  };
}

type RouteProps = RouteProp<ReviewStackParamList, 'ReviewDetail'>;
type NavigationProps = StackNavigationProp<ReviewStackParamList, 'ReviewDetail'>;

const ReviewDetailScreen: React.FC = () => {
  const route = useRoute<RouteProps>();
  const navigation = useNavigation<NavigationProps>();
  const dispatch = useDispatch<AppDispatch>();

  const { reviewId } = route.params;
  const { isLoadingReview, templates, onDemandItems, isLoadingOnDemand, error, deletedReviewIds } = useSelector((state: RootState) => state.review);
  const isOnline = useSelector((state: RootState) => state.offline?.isOnline ?? true);
  const review = useSelector((state: RootState) => {
    const foundReview = state.review.reviews.find(r => r.id === Number(reviewId));
    return foundReview;
  });

  // Ref to track current review for focus callback (avoids stale closure)
  const reviewRef = useRef(review);
  useEffect(() => {
    reviewRef.current = review;
  }, [review]);

  // Computed flag: review exists but lacks template data and is currently loading
  // This indicates a "cold load" where template needs to be fetched for the first time
  const isHydratingTemplate = Boolean(
    review &&
    !review.template?.categories?.length &&
    isLoadingReview &&
    isOnline  // Only show spinner for online loads (offline loads are instant from cache)
  );

  // Get template from store if review doesn't have it embedded
  const template = review?.template || (review?.templateId ? 
    templates.find(t => t.id === review.templateId) : null);
  
  // Use categories from review if available, otherwise from template
  // Check both review.template.categories and review.reviewData.template.categories (web structure)
  const baseCategories = review?.template?.categories ||
                    (review as any)?.reviewData?.template?.categories ||
                    template?.categories || [];

  // Merge on-demand items into their assigned categories
  const mergeOnDemandItemsIntoCategories = (cats: any[]): any[] => {
    const onDemandList = onDemandItems?.[Number(reviewId)] || [];

    // If no on-demand items, return categories as-is
    if (!onDemandList || onDemandList.length === 0) {
      return cats;
    }

    // Create a deep copy of categories to avoid mutation
    const categoriesCopy = JSON.parse(JSON.stringify(cats));

    // Group on-demand items by category
    const onDemandByCategory: { [key: number]: any[] } = {};

    onDemandList.forEach((item: any) => {
      if (item.categoryId) {
        if (!onDemandByCategory[item.categoryId]) {
          onDemandByCategory[item.categoryId] = [];
        }
        onDemandByCategory[item.categoryId].push(item);
      }
    });

    // Recursively merge on-demand items into categories
    const mergeIntoCategory = (category: any): any => {
      // Add on-demand items to this category if they belong here
      if (onDemandByCategory[category.id]) {
        const existingItems = category.items || [];

        // Filter out items that are already in the category to prevent duplicates
        // (persisted review state can already include on-demand items in the template)
        const newItems = onDemandByCategory[category.id].filter((newItem: any) => {
          return !existingItems.some((existing: any) => {
            // Check by ID (could be positive or negative for offline items)
            if (newItem.id && existing.id && newItem.id === existing.id) {
              return true;
            }

            // Check by tempId (for offline-created items)
            if (newItem.tempId && existing.tempId && newItem.tempId === existing.tempId) {
              return true;
            }

            // Check by normalized name (case-insensitive, trimmed)
            const newName = (newItem.name || '').toLowerCase().trim();
            const existingName = (existing.name || '').toLowerCase().trim();
            if (newName && existingName && newName === existingName && existing.isOnDemand) {
              return true;
            }

            return false;
          });
        });

        category.items = [...existingItems, ...newItems];
      }

      // Process subcategories
      if (category.subcategories) {
        category.subcategories = category.subcategories.map(mergeIntoCategory);
      }

      return category;
    };

    return categoriesCopy.map(mergeIntoCategory);
  };

  const categories = mergeOnDemandItemsIntoCategories(baseCategories);

  // IMPORTANT: Define all hooks before any early returns to comply with Rules of Hooks
  // handleItemPress must be memoized before hamburgerActions uses it
  const handleItemPress = useCallback((itemId: number, categoryName: string) => {
    // Add delay to prevent iOS crashes during navigation
    setTimeout(() => {
      try {
        navigation.navigate('ItemDetail', { reviewId, itemId, categoryName });
      } catch (navError) {
        console.error('[ReviewDetailScreen] Navigation error:', navError);
        // Try again after a longer delay
        setTimeout(() => {
          try {
            navigation.navigate('ItemDetail', { reviewId, itemId, categoryName });
          } catch (retryError) {
            console.error('[ReviewDetailScreen] Retry navigation failed:', retryError);
          }
        }, 500);
      }
    }, 150); // Keep existing 150ms delay for iOS crash avoidance
  }, [navigation, reviewId]);

  // Build hamburger actions from template (after handleItemPress is defined)
  const hamburgerActions = useMemo(
    () => buildHamburgerActions(categories, handleItemPress),
    [categories, handleItemPress]
  );

  // DEBUG: Log final item counts (will use getAllItems after it's defined below)
  // Note: This log runs on every render, but that's OK for debugging

  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showCompleteConfirmationModal, setShowCompleteConfirmationModal] = useState(false);
  const [quickSearchVisible, setQuickSearchVisible] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showOnDemandModal, setShowOnDemandModal] = useState(false);
  const [showGeneralNotesModal, setShowGeneralNotesModal] = useState(false);
  const [showOnDemandProjectsModal, setShowOnDemandProjectsModal] = useState(false);
  const [showReviewDetailsModal, setShowReviewDetailsModal] = useState(false);
  const [isSavingReviewDetails, setIsSavingReviewDetails] = useState(false);
  const [reportDeliveryStatus, setReportDeliveryStatus] = useState<string | null>(null);

  // Get safe area insets for back button positioning
  const insets = useSafeAreaInsets();

  // Set up navigation header with back button
  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft: (props) => (
        <HeaderBackButton
          {...props}
          onPress={() => navigation.navigate('ReviewList')}
        />
      ),
      gestureEnabled: true, // Enable swipe gesture for iPad
    });
  }, [navigation]);

  useEffect(() => {
    const loadData = async () => {
      if (isOnline) {
        // The review from list doesn't include categories
        dispatch(fetchReviewById({ reviewId: Number(reviewId), includeTemplate: true }));
      }
      // On-demand items are already fetched with the review details, no need for separate call
      setHasInitialized(true);
    };
    loadData();
  }, [reviewId, dispatch, isOnline]);

  // Refresh review when screen comes back into focus (e.g., returning from ItemDetail)
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      // Check if this review was recently deleted
      if (deletedReviewIds?.includes(Number(reviewId))) {
        console.warn('[ReviewDetailScreen] Review was deleted, navigating back to list');
        // Show toast notification
        if (typeof window !== 'undefined' && typeof window.alert === 'function') {
          setTimeout(() => {
            window.alert('This review has been deleted');
          }, 100);
        } else if (Alert && Alert.alert) {
          Alert.alert('Review Deleted', 'This review has been deleted');
        }
        navigation.navigate('ReviewList');
        return;
      }

      // Skip cache reload if review is already hydrated in memory (has categories)
      // This prevents stale cache from overwriting fresh data on focus (race condition fix)
      const currentReview = reviewRef.current;
      const isHydrated = (currentReview?.template?.categories?.length || 0) > 0;

      if (isOnline) {
        // Refresh review data to get updated response statuses
        // Wrap in try-catch to handle 404 errors
        const shouldIncludeTemplate = !isHydrated;
        dispatch(fetchReviewById({ reviewId: Number(reviewId), includeTemplate: shouldIncludeTemplate }))
          .unwrap()
          .catch((error) => {
            // Check if this is a 404 "Resource not found" error
            if (error?.message && (
              error.message.includes('Resource not found') ||
              error.message.includes('404') ||
              error.message.includes('not found')
            )) {
              console.warn('[ReviewDetailScreen] Review not found (404), navigating back to list');
              // Show toast notification
              if (typeof window !== 'undefined' && typeof window.alert === 'function') {
                setTimeout(() => {
                  window.alert('This review no longer exists');
                }, 100);
              } else if (Alert && Alert.alert) {
                Alert.alert('Review Not Found', 'This review no longer exists');
              }
              navigation.navigate('ReviewList');
            }
          });
      }

      // Re-set navigation options to ensure back button is visible
      // This is needed when returning from screens that hide the header
      navigation.setOptions({
        headerLeft: () => (
          <Button
            testID="review-detail-back-button"
            onPress={() => navigation.navigate('ReviewList')}
            mode="text"
            compact
            style={{ marginLeft: Platform.OS === 'web' ? 0 : -8 }}
          >
            ← Back
          </Button>
        ),
      });
    });

    return unsubscribe;
  }, [navigation, reviewId, dispatch, isOnline, deletedReviewIds]);


  useEffect(() => {
    if (review) {
      
      // If template is missing but we have templateId, fetch template details
      if (!review.template && !template && review.templateId) {
        dispatch(fetchTemplateDetails(review.templateId));
      }
    }
  }, [review, dispatch, template]);

  // Get all category IDs (including subcategories) for expand/collapse all
  // MUST be before early returns to satisfy React Rules of Hooks
  const getAllCategoryIds = useMemo(() => {
    const collectIds = (cats: any[]): number[] => {
      const ids: number[] = [];
      cats.forEach(cat => {
        ids.push(cat.id);
        if (cat.subcategories && cat.subcategories.length > 0) {
          ids.push(...collectIds(cat.subcategories));
        }
      });
      return ids;
    };

    const allIds = collectIds(categories);

    // Add Custom Items sentinel ID if there are on-demand items not in categories
    if (onDemandItems?.[Number(reviewId)]?.length > 0) {
      allIds.push(-1);
    }

    return allIds;
  }, [categories, onDemandItems, reviewId]);

  // Toggle expand/collapse all categories
  const handleToggleExpandAll = () => {
    const allExpanded = getAllCategoryIds.every(id => expandedCategories.includes(id));
    if (allExpanded) {
      // Collapse all
      setExpandedCategories([]);
    } else {
      // Expand all
      setExpandedCategories(getAllCategoryIds);
    }
  };

  // Check if all categories are expanded
  const allExpanded = getAllCategoryIds.every(id => expandedCategories.includes(id));

  // Show error state if there's an error and we're done loading
  if (error && !isLoadingReview && hasInitialized) {
    return (
      <LoadingScreen
        message="Error Loading Review"
        subtitle={error || "Unable to load review. Please check your connection and try again."}
      />
    );
  }

  // Show loading state ONLY if we have no cached review and are still loading
  // This prevents blocking the UI with a spinner during background refetches
  if (!review && isLoadingReview) {
    return (
      <LoadingScreen
        message="Loading Review..."
        subtitle="Loading review data..."
      />
    );
  }

  // Only show "not found" if we've initialized and there's no review and no error
  if (!review && hasInitialized && !error) {
    return (
      <LoadingScreen
        message="Review Not Found"
        subtitle={Platform.OS === 'web' ? "The review could not be found." : "This review is not available on this device yet. Connect to the internet and try again."}
      />
    );
  }

  // Final guard to ensure review is defined before computing derived values
  // This prevents crashes when accessing review.status and other properties
  if (!review) {
    return (
      <LoadingScreen
        message="Loading Review..."
        subtitle="Initializing review data..."
      />
    );
  }

  // Show partial loading UI if review exists but template is still being fetched (cold load)
  // Keep header visible for better UX while items section loads
  if (isHydratingTemplate) {
    return (
      <View testID="review-detail-screen" style={styles.container}>
        <SyncStatusBar showDetails compact={false} />
        <StuckSyncWarning reviewId={reviewId} />
        <ScrollView testID="review-detail-scroll" contentContainerStyle={styles.scrollContent}>
          {/* Show header with address and status */}
          <Card style={styles.headerCard}>
            <Card.Content>
              <Text style={styles.propertyAddress}>
                {review.property?.address || review.address || 'No address'}
              </Text>
              <Chip
                mode="outlined"
                style={styles.statusChipInline}
              >
                {review.status}
              </Chip>
            </Card.Content>
          </Card>

          {/* Show loading spinner for items section */}
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={styles.loadingText}>Loading review items...</Text>
            <Text style={styles.loadingSubtext}>
              First load may take a moment for large templates
            </Text>
          </View>
        </ScrollView>

        <ExpandableFAB
          actions={[
            {
              id: 'search',
              icon: 'magnify',
              label: 'Search',
              onPress: () => setShowSearchModal(true),
            },
            {
              id: 'add-item',
              icon: 'plus',
              label: 'Add Item',
              onPress: () => setShowOnDemandModal(true),
            },
            {
              id: 'notes',
              icon: 'note-text',
              label: 'Notes',
              onPress: () => setShowGeneralNotesModal(true),
            },
            {
              id: 'on-demand-projects',
              icon: 'hammer-wrench',
              label: 'On Demand Projects',
              onPress: () => setShowOnDemandProjectsModal(true),
            },
          ]}
          style={styles.fab}
        />

        <FAB
          testID="review-expand-all-button"
          icon="arrow-expand-vertical"
          onPress={handleToggleExpandAll}
          style={[styles.expandAllFab, { bottom: insets.bottom + 16 }]}
          size="small"
        />
      </View>
    );
  }

  // Helper function to recursively get all items from categories and subcategories
  const getAllItems = (categoryList: any[]): any[] => {
    const allItems: any[] = [];
    categoryList.forEach(category => {
      // Add items from this category
      const categoryItems = category.items || [];
      allItems.push(...categoryItems);
      
      // Recursively add items from subcategories
      const subcategories = category.subcategories || [];
      allItems.push(...getAllItems(subcategories));
    });
    return allItems;
  };

  const getProgress = () => {
    try {
      if (!review?.responses || categories.length === 0) {
        return 0;
      }
      const allItems = getAllItems(categories);

      const completed = allItems.filter(item =>
        getResponseStatus(item.id) === ResponseStatusValues.COMPLETED
      ).length;
      return allItems.length > 0 ? completed / allItems.length : 0;
    } catch (error) {
      console.error('Error in getProgress:', error);
      return 0;
    }
  };

  const getValidationStatus = () => {
    try {
      const defaultStats = {
        isValid: false,
        canSubmit: false,
        issues: [],
        stats: { total: 0, completed: 0, inProgress: 0, notStarted: 0, requiredIncomplete: 0 }
      };
      
      if (categories.length === 0) return defaultStats;
      
      const issues: string[] = [];
      const allItems = getAllItems(categories);
      
      // Since everything is required, check if all items are completed
      const incompleteItems = allItems.filter(item => 
        getResponseStatus(item.id) !== ResponseStatusValues.COMPLETED
      );
      
      if (incompleteItems.length > 0) {
        issues.push(`${incompleteItems.length} items incomplete`);
      }
      
      return {
        isValid: incompleteItems.length === 0,
        canSubmit: incompleteItems.length === 0,
        issues,
        stats: {
          total: allItems.length,
          completed: allItems.filter(item => getResponseStatus(item.id) === ResponseStatusValues.COMPLETED).length,
          inProgress: allItems.filter(item => getResponseStatus(item.id) === ResponseStatusValues.IN_PROGRESS).length,
          notStarted: allItems.filter(item => getResponseStatus(item.id) === ResponseStatusValues.NOT_STARTED).length,
          requiredIncomplete: incompleteItems.length,
        }
      };
    } catch (error) {
      console.error('Error in getValidationStatus:', error);
      return {
        isValid: false,
        canSubmit: false,
        issues: ['Error calculating validation status'],
        stats: { total: 0, completed: 0, inProgress: 0, notStarted: 0, requiredIncomplete: 0 }
      };
    }
  };

  const getResponseStatus = (itemId: number) => {
    const response = selectBestResponseForItem(review.responses, itemId);
    return response?.status || ResponseStatusValues.NOT_STARTED;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case ResponseStatusValues.NOT_STARTED:
        return colors.statusNotStarted;
      case ResponseStatusValues.IN_PROGRESS:
        return colors.statusInProgress;
      case ResponseStatusValues.COMPLETED:
        return colors.statusCompleted;
      default:
        return colors.gray500;
    }
  };

  const toggleCategory = (categoryId: number) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSubmitReview = () => {
    const homeManagerEmail = review?.homeManagerEmail?.trim() || '';
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(homeManagerEmail);
    if (!validEmail) {
      Alert.alert(
        'Home manager email required',
        'Add a valid home manager email in Review details before submitting.'
      );
      setShowReviewDetailsModal(true);
      return;
    }

    const validation = getValidationStatus();

    if (!validation.canSubmit) {
      const issuesList = validation.issues.join('\n• ');
      Alert.alert(
        'Cannot Submit Review',
        `Please address the following issues:\n\n• ${issuesList}`,
        [{ text: 'OK' }]
      );
      return;
    }

    setShowConfirmationModal(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowConfirmationModal(false);

    try {
      const useLocalFirstWorkflow = shouldUseOfflineReviewWorkflow();
      await dispatch(submitReview({
        reviewId
      })).unwrap();

      Alert.alert(
        'Success',
        useLocalFirstWorkflow
          ? 'Review marked submitted. It will sync shortly.'
          : 'Review submitted successfully!'
      );
    } catch (error) {
      console.error('Submit review error:', error);
      Alert.alert('Error', `Failed to submit review: ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  const handleCompleteReview = () => {
    setShowCompleteConfirmationModal(true);
  };

  const handleConfirmedComplete = async () => {
    setShowCompleteConfirmationModal(false);

    try {
      const useLocalFirstWorkflow = shouldUseOfflineReviewWorkflow();
      await dispatch(completeReview(Number(reviewId))).unwrap();

      // Native local-first completion is intentionally queued before the next sync.
      // A server refresh here can overwrite that queued lifecycle state with the
      // previous submitted status before the sync has sent it.
      const isOnline = await getEffectiveNetworkOnline();

      if (isOnline && !useLocalFirstWorkflow) {
        // Refresh the review data to show updated status from server
        await dispatch(fetchReviewById({ reviewId: Number(reviewId), includeTemplate: false })).unwrap();
      }

      Alert.alert(
        'Success',
        useLocalFirstWorkflow
          ? 'Review marked complete. It will sync shortly.'
          : 'Review completed successfully!'
      );
    } catch (error) {
      console.error('Complete review error:', error);
      Alert.alert('Error', `Failed to complete review: ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  const handleSearchItemSelect = (item: ReviewItem & { categoryName?: string }) => {
    const itemId = toNavigableReviewItemId(item.id);
    if (itemId === null) {
      console.error('[ReviewDetailScreen] Search result has an invalid item ID', item.id);
      return;
    }

    setShowSearchModal(false);
    handleItemPress(itemId, item.categoryName || 'Unknown');
  };

  const handleCreateOnDemandItem = async (itemData: any) => {
    try {
      const newItem = await dispatch(createOnDemandItem({
        reviewId: Number(reviewId),
        data: itemData
      })).unwrap();

      setShowOnDemandModal(false);

      // Refresh the review to get updated on-demand items
      await dispatch(fetchReviewById({ reviewId: Number(reviewId), includeTemplate: false }));

      // Navigate to the newly created item
      if (newItem && newItem.id) {
        handleItemPress(newItem.id, newItem.category?.name || 'Custom');
      } else {
        Alert.alert('Success', 'Custom item created successfully!');
      }
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to create custom item';
      Alert.alert('Error', errorMessage);
    }
  };

  const handleSaveGeneralNotes = async (notes: string) => {
    try {
      await dispatch(updateReviewNotes({ 
        reviewId: Number(reviewId), 
        notes 
      })).unwrap();
      setShowGeneralNotesModal(false);
    } catch (error) {
      console.error('Failed to update notes:', error);
      Alert.alert('Error', 'Failed to save notes. Please try again.');
    }
  };

  const handleSaveOnDemandProjects = async (onDemandProjects: string) => {
    try {
      await dispatch(updateReviewOnDemandProjects({
        reviewId: Number(reviewId),
        onDemandProjects
      })).unwrap();
      setShowOnDemandProjectsModal(false);
    } catch (error) {
      console.error('Failed to update On Demand Projects:', error);
      Alert.alert('Error', 'Failed to save On Demand Projects. Please try again.');
    }
  };

  const handleSaveReviewDetails = async (details: ReviewContactDetails) => {
    setIsSavingReviewDetails(true);
    try {
      await dispatch(updateReviewDetails({ reviewId: Number(reviewId), details })).unwrap();
      setShowReviewDetailsModal(false);
    } catch (saveError) {
      Alert.alert('Error', 'Failed to save review details. Please try again.');
      throw saveError;
    } finally {
      setIsSavingReviewDetails(false);
    }
  };

  const openReviewDetails = async () => {
    setShowReviewDetailsModal(true);
    if (review.status !== ReviewStatusValues.SUBMITTED && review.status !== ReviewStatusValues.COMPLETED) {
      return;
    }

    try {
      const deliveries = await reviewService.getReviewReportDeliveries(Number(reviewId));
      setReportDeliveryStatus(
        deliveries.length > 0
          ? deliveries.map(delivery => delivery.status).join(', ')
          : 'Not sent'
      );
    } catch {
      setReportDeliveryStatus('Unavailable');
    }
  };

  const handleSaveAndResend = async (details: ReviewContactDetails) => {
    await handleSaveReviewDetails(details);
    try {
      const resend = await reviewService.resendReviewReportWithDetails(
        Number(reviewId),
        details
      );
      if (!resend.queued) {
        setReportDeliveryStatus('Saved offline — not queued');
        Alert.alert(
          'Details saved offline',
          'Reconnect and choose “Save and resend report” again. No report was queued while offline.'
        );
        return;
      }

      setReportDeliveryStatus('Queued');
      Alert.alert(
        'Report queued',
        resend.result.message || 'The report will be resent to its recipients.'
      );
    } catch (resendError) {
      Alert.alert('Resend failed', 'Details were saved, but the report could not be queued for resend.');
      throw resendError;
    }
  };


  // Helper function to calculate completion stats for nested categories
  const getCategoryStats = (category: any): { completed: number; total: number } => {
    let completed = 0;
    let total = 0;

    // Count all items in this category including on-demand items
    const categoryItems = category.items || [];
    total += categoryItems.length;
    completed += categoryItems.filter((item: any) =>
      getResponseStatus(item.id) === ResponseStatusValues.COMPLETED
    ).length;

    // Recursively count items in subcategories
    const subcategories = category.subcategories || [];
    subcategories.forEach((subcat: any) => {
      const subcatStats = getCategoryStats(subcat);
      completed += subcatStats.completed;
      total += subcatStats.total;
    });

    return { completed, total };
  };

  // Recursive component for rendering categories
  const CategoryTree: React.FC<{ category: any; level?: number }> = ({ category, level = 0 }) => {
    const categoryStats = getCategoryStats(category);
    // Keep all items including on-demand items - they should appear in both places
    const categoryItems = category.items || [];
    const subcategories = category.subcategories || [];
    const isExpanded = expandedCategories.includes(category.id);
    const hasSubcategories = subcategories.length > 0;
    const hasItems = categoryItems.length > 0;

    return (
      <Card key={category.id} style={[styles.categoryCard, { marginLeft: level * 16 }]}>
        <List.Accordion
          testID={`review-category-${category.id}`}
          title={category.name}
          description={`${categoryStats.completed}/${categoryStats.total} completed`}
          expanded={isExpanded}
          onPress={() => toggleCategory(category.id)}
          style={level === 0 ? styles.topLevelCategoryAccordion : undefined}
          left={props => (
            <List.Icon
              {...props}
              icon={category.icon || (hasSubcategories ? 'folder-multiple' : 'folder')}
              color={
                categoryStats.completed === categoryStats.total
                  ? colors.success
                  : categoryStats.completed > 0
                  ? colors.warning
                  : colors.error
              }
            />
          )}
        >
          {/* Render subcategories first */}
          {hasSubcategories && subcategories.map((subcat: any) => (
            <CategoryTree key={subcat.id} category={subcat} level={level + 1} />
          ))}
          
          {/* Then render items in this category */}
          {hasItems && categoryItems.map((item: any, index: number) => {
            const status = getResponseStatus(item.id);
            return (
              <TouchableOpacity
                testID={`review-item-${item.id}`}
                key={item.id}
                onPress={() => handleItemPress(item.id, category.name)}
                activeOpacity={0.85}
              >
                <List.Item
                  testID={`review-item-content-${item.id}`}
                  title={item.name}
                  description={item.description}
                  onPress={() => handleItemPress(item.id, category.name)}
                  style={{ marginLeft: 24 + (level * 16) }}
                  left={() => (
                    <View style={styles.itemStatus}>
                      <MaterialCommunityIcons
                        testID={`review-item-status-${item.id}-${status}`}
                        name={
                          status === ResponseStatusValues.COMPLETED
                            ? 'check-circle'
                            : status === ResponseStatusValues.IN_PROGRESS
                            ? 'progress-clock'
                            : 'circle-outline'
                        }
                        size={24}
                        color={getStatusColor(status)}
                      />
                    </View>
                  )}
                  right={props => (
                    <List.Icon {...props} icon="chevron-right" />
                  )}
                />
              </TouchableOpacity>
            );
          })}
        </List.Accordion>
      </Card>
    );
  };

  const progress = getProgress();
  const validation = getValidationStatus();
  const canSubmit =
    review.status !== ReviewStatusValues.SUBMITTED &&
    review.status !== ReviewStatusValues.COMPLETED &&
    validation.canSubmit;


  return (
    <View testID="review-detail-screen" style={styles.container}>
      {/* Back button fallback for iPad */}
      {Platform.OS === 'ios' && (
        <View style={[styles.backButtonContainer, { top: insets.top + 10 }]}>
          <IconButton
            testID="review-detail-back-button"
            icon="arrow-left"
            size={24}
            onPress={() => navigation.navigate('ReviewList')}
          />
        </View>
      )}

      <SyncStatusBar showDetails compact={false} />
      <StuckSyncWarning reviewId={reviewId} />
      <ScrollView 
        testID="review-detail-scroll"
        contentContainerStyle={styles.scrollContent}
        scrollEnabled={Platform.OS !== 'web' || !quickSearchVisible}
      >
        {/* Review Header */}
        <Card style={styles.headerCard}>
          <Card.Content>
            <Text style={styles.propertyAddress}>
              {review.property?.address || 'No address'}
            </Text>
            <View style={styles.memberDetailsRow}>
              <View style={styles.memberDetailsText}>
                <Text testID="review-detail-member-name" style={styles.memberName}>
                  {review.memberName || 'Member name not set'}
                </Text>
                {review.homeManagerName ? (
                  <Text style={styles.memberSecondary}>Home manager: {review.homeManagerName}</Text>
                ) : null}
              </View>
              <Button
                testID="edit-review-details-button"
                mode="outlined"
                icon="account-edit-outline"
                compact
                onPress={openReviewDetails}
              >
                Edit details
              </Button>
            </View>
            {template?.name && (
              <Text style={styles.propertyType}>
                Type: {template.name}
              </Text>
            )}
            
            <View style={styles.progressSection}>
              <View style={styles.progressHeader}>
                <Text style={styles.progressLabel}>Overall Progress</Text>
                <Text style={styles.progressText}>
                  {Math.round(progress * 100)}%
                </Text>
              </View>
              <ProgressBar
                progress={progress}
                color={colors.primary}
                style={styles.progressBar}
              />
            </View>

            {review.status === ReviewStatusValues.SUBMITTED && review.submittedAt && (
              <Chip
                mode="flat"
                style={[styles.statusChip, { backgroundColor: colors.success }]}
                textStyle={{ color: '#fff' }}
                icon="check-circle"
              >
                Submitted on {new Date(review.submittedAt).toLocaleDateString()}
              </Chip>
            )}
            
            {/* Validation Status */}
            {review.status !== ReviewStatusValues.SUBMITTED && (
              <View style={styles.validationSection}>
                <Text style={styles.validationTitle}>Review Status</Text>
                <View style={styles.validationStats}>
                  <View style={styles.statItem}>
                    <MaterialCommunityIcons 
                      name="check-circle" 
                      size={16} 
                      color={colors.success} 
                    />
                    <Text style={styles.statText}>{validation.stats?.completed || 0} Complete</Text>
                  </View>
                  <View style={styles.statItem}>
                    <MaterialCommunityIcons 
                      name="progress-clock" 
                      size={16} 
                      color={colors.warning} 
                    />
                    <Text style={styles.statText}>{validation.stats?.inProgress || 0} In Progress</Text>
                  </View>
                  <View style={styles.statItem}>
                    <MaterialCommunityIcons 
                      name="circle-outline" 
                      size={16} 
                      color={colors.error} 
                    />
                    <Text style={styles.statText}>{validation.stats?.notStarted || 0} Not Started</Text>
                  </View>
                </View>
                
                {validation.issues.length > 0 && (
                  <View style={styles.issuesContainer}>
                    {validation.issues.map((issue, index) => (
                      <View key={index} style={styles.issueItem}>
                        <MaterialCommunityIcons 
                          name="alert-circle" 
                          size={14} 
                          color={colors.error} 
                        />
                        <Text style={styles.issueText}>{issue}</Text>
                      </View>
                    ))}
                  </View>
                )}
                
                {validation.canSubmit && (
                  <Chip
                    mode="flat"
                    style={[styles.statusChip, { backgroundColor: colors.success }]}
                    textStyle={{ color: '#fff' }}
                    icon="check-all"
                  >
                    Ready to Submit
                  </Chip>
                )}
              </View>
            )}
          </Card.Content>
        </Card>

        {/* Quick Search - Web only */}
        {Platform.OS === 'web' && (
          <Card style={styles.searchCard}>
            <Card.Content>
              <SearchBar
                reviewId={reviewId}
                onItemSelect={handleSearchItemSelect}
                placeholder="Quick search for items..."
                style={styles.quickSearchBar}
                onResultsVisibilityChange={setQuickSearchVisible}
                review={review}
                additionalItems={onDemandItems?.[Number(reviewId)] || []}
              />
            </Card.Content>
          </Card>
        )}

        {/* Categories - Only show top-level categories */}
        {categories
          .filter(category => !category.parentCategoryId)
          .map(category => (
            <CategoryTree key={category.id} category={category} level={0} />
          ))}

        {/* Custom Items Section - shows all on-demand items */}
        {onDemandItems && onDemandItems[Number(reviewId)] && onDemandItems[Number(reviewId)].length > 0 && (() => {
          const onDemandList = onDemandItems[Number(reviewId)] || [];
          const completedOnDemand = onDemandList.filter((item: any) =>
            getResponseStatus(item.id) === ResponseStatusValues.COMPLETED
          ).length;
          const totalOnDemand = onDemandList.length;
          const allCompleted = completedOnDemand === totalOnDemand && totalOnDemand > 0;

          return (
            <Card style={[styles.categoryCard, { marginLeft: 0 }]}>
              <List.Accordion
                testID="review-category-custom-items"
                title="Custom Items"
                description={`${completedOnDemand}/${totalOnDemand} completed`}
                expanded={expandedCategories.includes(-1)}
                onPress={() => toggleCategory(-1)}
                left={props => (
                  <List.Icon
                    {...props}
                    icon={allCompleted ? 'check-circle' : 'plus-box'}
                    color={allCompleted ? colors.success : colors.secondary}
                  />
                )}
              >
              {onDemandList.map((item: any) => {
                const status = getResponseStatus(item.id);
                const icon = status === ResponseStatusValues.COMPLETED
                  ? 'check-circle'
                  : status === ResponseStatusValues.IN_PROGRESS
                  ? 'circle-outline'
                  : 'circle-outline';
                const iconColor = status === ResponseStatusValues.COMPLETED
                  ? colors.success
                  : status === ResponseStatusValues.IN_PROGRESS
                  ? colors.warning
                  : colors.textSecondary;

                return (
                  <TouchableOpacity
                    testID={`review-item-${item.id}`}
                    key={item.id}
                    onPress={() => handleItemPress(item.id, 'Custom Items')}
                    activeOpacity={0.85}
                  >
                    <List.Item
                      testID={`review-item-content-${item.id}`}
                      title={item.name}
                      description={item.description || 'Custom inspection item'}
                      onPress={() => handleItemPress(item.id, 'Custom Items')}
                      left={props => (
                        <View testID={`review-item-status-${item.id}-${status}`}>
                          <List.Icon
                            {...props}
                            icon={icon}
                            color={iconColor}
                          />
                        </View>
                      )}
                      right={props => (
                        <MaterialCommunityIcons
                          name="chevron-right"
                          size={24}
                          color={colors.textSecondary}
                        />
                      )}
                      style={{ marginLeft: 24 }}
                    />
                  </TouchableOpacity>
                );
              })}
              </List.Accordion>
            </Card>
          );
        })()}

        {/* Submit Button */}
        {canSubmit && (
          <Button
            testID="submit-review-button"
            mode="contained"
            onPress={handleSubmitReview}
            style={styles.submitButton}
            icon="check-all"
          >
            Submit Review
          </Button>
        )}

        {/* Vendor Emails and Complete Review Buttons - only show for submitted reviews */}
        {review.status === ReviewStatusValues.SUBMITTED && (
          <View style={styles.vendorEmailSection}>
            <Button
              testID="vendor-emails-button"
              mode="contained"
              onPress={() => navigation.navigate('VendorEmails', { reviewId: Number(reviewId) })}
              style={styles.vendorEmailButton}
              icon="email-outline"
            >
              Vendor Emails
            </Button>
            <Button
              testID="complete-review-button"
              mode="contained"
              onPress={handleCompleteReview}
              style={[styles.vendorEmailButton, { marginTop: spacing.sm }]}
              icon="check-circle"
            >
              Complete Review
            </Button>
          </View>
        )}
      </ScrollView>

      {/* Expandable FAB */}
      <ExpandableFAB
        actions={[
          {
            id: 'search',
            icon: 'magnify',
            label: 'Search',
            onPress: () => setShowSearchModal(true),
          },
          {
            id: 'add-item',
            icon: 'plus',
            label: 'Add Item',
            onPress: () => setShowOnDemandModal(true),
          },
          {
            id: 'notes',
            icon: 'note-text',
            label: 'Notes',
            onPress: () => setShowGeneralNotesModal(true),
          },
          {
            id: 'on-demand-projects',
            icon: 'hammer-wrench',
            label: 'On Demand Projects',
            onPress: () => setShowOnDemandProjectsModal(true),
          },
          ...hamburgerActions,
        ]}
        style={styles.fab}
      />

      {/* Expand/Collapse All FAB */}
      <FAB
        testID="review-expand-all-button"
        icon={allExpanded ? 'arrow-collapse-vertical' : 'arrow-expand-vertical'}
        onPress={handleToggleExpandAll}
        style={[styles.expandAllFab, { bottom: insets.bottom + 16 }]}
        size="small"
      />

      {/* On-Demand Item Modal */}
      <OnDemandItemModal
        visible={showOnDemandModal}
        onDismiss={() => setShowOnDemandModal(false)}
        onSave={handleCreateOnDemandItem}
        categories={categories}
        loading={isLoadingOnDemand}
      />

      {/* General Notes Modal */}
      <GeneralNotesModal
        visible={showGeneralNotesModal}
        onDismiss={() => setShowGeneralNotesModal(false)}
        onSave={handleSaveGeneralNotes}
        initialNotes={review?.notes || ''}
        loading={isLoadingReview}
      />

      {/* On Demand Projects Modal */}
      <OnDemandProjectsModal
        visible={showOnDemandProjectsModal}
        onDismiss={() => setShowOnDemandProjectsModal(false)}
        onSave={handleSaveOnDemandProjects}
        initialOnDemandProjects={review?.onDemandProjects || ''}
        loading={isLoadingReview}
      />

      <ReviewDetailsModal
        visible={showReviewDetailsModal}
        initialDetails={{
          memberName: review.memberName || '',
          memberEmail: review.memberEmail,
          homeManagerName: review.homeManagerName,
          homeManagerEmail: review.homeManagerEmail,
        }}
        loading={isSavingReviewDetails}
        showResend={review.status === ReviewStatusValues.SUBMITTED || review.status === ReviewStatusValues.COMPLETED}
        deliveryStatus={reportDeliveryStatus}
        onDismiss={() => setShowReviewDetailsModal(false)}
        onSave={handleSaveReviewDetails}
        onResendRequested={handleSaveAndResend}
      />

      {/* Search Modal */}
      <Portal>
        <Modal
          visible={showSearchModal}
          onDismiss={() => setShowSearchModal(false)}
          contentContainerStyle={styles.modalContent}
        >
          <KeyboardAvoidingView 
            testID="review-search-modal"
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={styles.modalInner}
            keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : -500}
          >
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Search Items</Text>
              <Button
                testID="review-search-modal-close-button"
                mode="text"
                onPress={() => setShowSearchModal(false)}
                icon="close"
                compact
              >
                Close
              </Button>
            </View>
            
            <View style={styles.modalSearchContainer}>
              <SearchBar
                testID="review-search-modal-input"
                reviewId={reviewId}
                onItemSelect={handleSearchItemSelect}
                placeholder="Search review items..."
                style={styles.modalSearchBar}
                useNavigationFocus={false}
                inModal={true}
                review={review}
                additionalItems={onDemandItems?.[Number(reviewId)] || []}
              />
            </View>
          </KeyboardAvoidingView>
        </Modal>
      </Portal>


      {/* Confirmation Modal */}
      <Portal>
        <Modal
          visible={showConfirmationModal}
          onDismiss={() => setShowConfirmationModal(false)}
          contentContainerStyle={styles.confirmationModal}
        >
          <View style={styles.confirmationContainer}>
            <Text style={styles.confirmationTitle}>Submit Review</Text>
            <Text style={styles.confirmationSubtitle}>
              Ready to submit review with {getValidationStatus().stats?.completed || 0}/{getValidationStatus().stats?.total || 0} items completed.
            </Text>
            <Text style={styles.confirmationWarning}>
              This action cannot be undone.
            </Text>
            
            <View style={styles.confirmationActions}>
              <Button
                testID="submit-review-cancel-button"
                mode="outlined"
                onPress={() => {
                  setShowConfirmationModal(false);
                }}
                style={styles.confirmationButton}
              >
                Cancel
              </Button>
              <Button
                testID="submit-review-confirm-button"
                mode="contained"
                onPress={() => {
                  handleConfirmedSubmit();
                }}
                style={styles.confirmationButton}
                icon="check-all"
              >
                Submit Review
              </Button>
            </View>
          </View>
        </Modal>
      </Portal>

      {/* Complete Review Confirmation Modal */}
      <Portal>
        <Modal
          visible={showCompleteConfirmationModal}
          onDismiss={() => setShowCompleteConfirmationModal(false)}
          contentContainerStyle={styles.confirmationModal}
        >
          <View style={styles.confirmationContainer}>
            <Text style={styles.confirmationTitle}>Complete Review</Text>
            <Text style={styles.confirmationSubtitle}>
              This will mark the review as completed without processing vendor emails.
            </Text>
            <Text style={styles.confirmationWarning}>
              You can still access vendor emails later if needed.
            </Text>

            <View style={styles.confirmationActions}>
              <Button
                testID="complete-review-cancel-button"
                mode="outlined"
                onPress={() => {
                  setShowCompleteConfirmationModal(false);
                }}
                style={styles.confirmationButton}
              >
                Cancel
              </Button>
              <Button
                testID="complete-review-confirm-button"
                mode="contained"
                onPress={() => {
                  handleConfirmedComplete();
                }}
                style={styles.confirmationButton}
                icon="check-circle"
              >
                Complete Review
              </Button>
            </View>
          </View>
        </Modal>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  backButtonContainer: {
    position: 'absolute',
    left: spacing.md,
    zIndex: 1000,
    backgroundColor: colors.background,
    borderRadius: 20,
    ...shadows.sm,
  },
  scrollContent: {
    paddingBottom: spacing.xxl,
  },
  headerCard: {
    margin: spacing.md,
    ...shadows.md,
  },
  searchCard: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.md,
    ...shadows.sm,
    zIndex: 100,
    elevation: 100,
  },
  quickSearchBar: {
    // No additional styling needed, inherits from SearchBar
  },
  propertyName: {
    ...typography.h3,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  propertyAddress: {
    ...typography.h4,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  memberDetailsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  memberDetailsText: {
    flex: 1,
  },
  memberName: {
    ...typography.body1,
    color: colors.text,
    fontWeight: '600',
  },
  memberSecondary: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  propertyType: {
    ...typography.caption,
    color: colors.primary,
    fontWeight: '500',
    marginBottom: spacing.md,
  },
  progressSection: {
    marginTop: spacing.md,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  progressLabel: {
    ...typography.body2,
    color: colors.text,
  },
  progressText: {
    ...typography.body2,
    color: colors.primary,
    fontWeight: '600',
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  statusChip: {
    marginTop: spacing.md,
    alignSelf: 'flex-start',
  },
  validationSection: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.gray200,
  },
  validationTitle: {
    ...typography.body1,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  validationStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: spacing.sm,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  statText: {
    ...typography.caption,
    marginLeft: spacing.xs,
    color: colors.text,
  },
  issuesContainer: {
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
  },
  issueItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  issueText: {
    ...typography.caption,
    marginLeft: spacing.xs,
    color: colors.error,
    flex: 1,
  },
  categoryCard: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
    ...shadows.sm,
  },
  topLevelCategoryAccordion: {
    backgroundColor: '#e2f5fa',
  },
  listItem: {
    paddingLeft: spacing.lg,
  },
  itemStatus: {
    marginLeft: spacing.md,
    marginRight: spacing.sm,
  },
  requiredChip: {
    marginRight: spacing.sm,
  },
  requiredText: {
    fontSize: 10,
    color: colors.error,
  },
  submitButton: {
    margin: spacing.md,
    marginTop: spacing.lg,
  },
  fab: {
    // The ExpandableFAB component handles its own positioning
  },
  expandAllFab: {
    position: 'absolute',
    left: 16,
    zIndex: 9,
  },
  modalContent: {
    backgroundColor: 'white',
    margin: spacing.md,
    marginTop: spacing.xxl * 2,
    borderRadius: 8,
    maxHeight: '85%',
    height: '85%',
  },
  modalInner: {
    flex: 1,
    padding: spacing.lg,
  },
  modalScrollContent: {
    padding: spacing.lg,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  modalTitle: {
    ...typography.h3,
    flex: 1,
  },
  searchBar: {
    marginTop: spacing.md,
  },
  modalSearchContainer: {
    flex: 1,
    marginTop: spacing.xs,
    overflow: 'visible',
  },
  modalSearchBar: {
    zIndex: 1,
  },
  confirmationModal: {
    backgroundColor: 'white',
    margin: spacing.lg,
    borderRadius: 8,
    maxHeight: '50%',
  },
  confirmationContainer: {
    padding: spacing.lg,
  },
  confirmationTitle: {
    ...typography.h3,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  confirmationSubtitle: {
    ...typography.body1,
    color: colors.text,
    marginBottom: spacing.md,
    textAlign: 'center',
  },
  confirmationWarning: {
    ...typography.body2,
    color: colors.error,
    marginBottom: spacing.lg,
    textAlign: 'center',
    fontWeight: '500',
  },
  confirmationActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  confirmationButton: {
    flex: 1,
    marginHorizontal: spacing.xs,
  },
  vendorEmailSection: {
    marginHorizontal: spacing.md,
    marginTop: spacing.md,
    gap: spacing.sm,
  },
  vendorEmailButton: {
    marginBottom: spacing.sm,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
    minHeight: 300,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: 18,
    fontWeight: '600' as const,
    color: colors.text,
  },
  loadingSubtext: {
    marginTop: spacing.sm,
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  statusChipInline: {
    alignSelf: 'flex-start',
    marginTop: spacing.sm,
  },
});

export default ReviewDetailScreen;
